import React, { useEffect, useState, useMemo } from "react";
import "leaflet/dist/leaflet.css";
import { MapContainer, TileLayer, Marker, Popup, Polyline } from "react-leaflet";
import L from "leaflet";
import { api } from "@/lib/api";
import { todayISO, formatGBP } from "@/lib/format";
import { MapPin } from "lucide-react";

// Fix default icon paths
const icon = (color) => L.divIcon({
  className: "",
  html: `<div style="background:${color};width:30px;height:30px;border-radius:50% 50% 50% 0;transform:rotate(-45deg);border:2px solid white;box-shadow:0 2px 6px rgba(0,0,0,0.2);display:flex;align-items:center;justify-content:center;"><div style="transform:rotate(45deg);color:white;font-weight:700;font-size:12px;">●</div></div>`,
  iconSize: [30, 30],
  iconAnchor: [15, 30],
});

const COLOR = { scheduled: "#1E90E6", completed: "#059669", no_access: "#e11d48", skipped: "#737373", rescheduled: "#d97706" };

export default function MapView() {
  const [jobs, setJobs] = useState([]);
  const [date, setDate] = useState(todayISO());
  const [mountId] = useState(() => Math.random().toString(36).slice(2));

  useEffect(() => {
    (async () => {
      const { data } = await api.get("/jobs", { params: { date_from: date, date_to: date } });
      setJobs([...data].sort((a, b) => (a.route_order || 0) - (b.route_order || 0)));
    })();
  }, [date]);

  const withCoords = useMemo(() => jobs.filter((j) => j.lat != null && j.lng != null), [jobs]);
  const center = withCoords.length ? [withCoords[0].lat, withCoords[0].lng] : [52.4862, -1.8904]; // Birmingham UK default

  const line = withCoords.map((j) => [j.lat, j.lng]);

  return (
    <div className="h-[calc(100vh-7rem)] md:h-[calc(100vh-4rem)] flex flex-col" data-testid="map-page">
      <div className="px-6 py-4 flex items-center justify-between border-b border-border">
        <div>
          <h1 className="font-display text-2xl font-bold">Today's map</h1>
          <div className="text-sm text-muted-foreground">{withCoords.length} of {jobs.length} jobs have coordinates</div>
        </div>
        <input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="rounded-md border border-input px-3 py-2 text-sm bg-transparent" data-testid="map-date" />
      </div>
      <div className="flex-1 relative" data-testid="map-container">
        <MapContainer key={mountId} center={center} zoom={11} scrollWheelZoom className="h-full w-full">
          <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" attribution='&copy; OpenStreetMap' />
          {withCoords.map((j, i) => (
            <Marker key={j.id} position={[j.lat, j.lng]} icon={icon(COLOR[j.status] || "#1E90E6")}>
              <Popup>
                <div className="font-semibold">#{i + 1} · {j.customer_name}</div>
                <div className="text-xs">{j.customer_address}</div>
                <div className="text-xs">{j.customer_postcode}</div>
                <div className="text-xs mt-1">{formatGBP(j.price)} · {j.status}</div>
                <a className="text-xs text-sky-600 underline" target="_blank" rel="noreferrer" href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent((j.customer_address || "") + " " + (j.customer_postcode || ""))}`}>Open in Google Maps</a>
              </Popup>
            </Marker>
          ))}
          {line.length > 1 && <Polyline positions={line} pathOptions={{ color: "#1E90E6", weight: 3, opacity: 0.6, dashArray: "6 8" }} />}
        </MapContainer>
        {withCoords.length === 0 && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <div className="rounded-xl bg-card border border-border p-6 text-center max-w-sm pointer-events-auto">
              <MapPin className="h-8 w-8 mx-auto text-muted-foreground" />
              <div className="mt-2 font-semibold">No mapped jobs for this date</div>
              <p className="text-sm text-muted-foreground mt-1">Add latitude/longitude to customers to see them here.</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
